import type { MaybeRef } from 'vue'
import type { AnalyticsState, CurrentSearchState, RenderedSearchState, TrackFunction } from './searchDiagnosticsSession'
import type { SearchDiagnosticResult } from '~/utils/pendo/search-diagnostics'
import { getCurrentInstance, onBeforeUnmount, toValue, watch } from 'vue'
import { MIN_GLOBAL_SEARCH_LENGTH } from '~/constants/search'
import {
  buildSearchFailedPayload,
  buildSearchNoResultsPayload,
  buildSearchReformulatedPayload,
  buildSearchResultClickedPayload,
  buildSearchResultsRenderedPayload,
  buildSearchSignature,
  createSearchId,
  SEARCH_DIAGNOSTICS_EVENTS,
} from '~/utils/pendo/search-diagnostics'
import {
  buildFailedSearchSignature,
  classifySearchError,
  clearRenderedSearchState,
  finalizeCurrentSearchSession,
  finalizeInvalidQuerySession,
  finalizePreviousSearchForNextState,
  normalizeSortOrder,
} from './searchDiagnosticsSession'

interface SearchDiagnosticsAnalyticsOptions {
  query: MaybeRef<string>
  page: MaybeRef<number>
  routeSearchSignature: MaybeRef<string>
  isLoading: MaybeRef<boolean>
  results: MaybeRef<SearchDiagnosticResult[]>
  totalResults: MaybeRef<number>
  rows: MaybeRef<number>
  hasLicense: MaybeRef<boolean>
  transportRetried?: MaybeRef<boolean>
  hasError?: MaybeRef<boolean>
  errorStatus?: MaybeRef<number | null | undefined>
  filterCount?: MaybeRef<number>
  filterSignature?: MaybeRef<string>
  sortOrder?: MaybeRef<string>
  now?: () => number
  random?: () => number
}

export function useSearchDiagnosticsAnalytics(options: SearchDiagnosticsAnalyticsOptions) {
  if (!import.meta.client) {
    return { trackResultClicked: () => {}, trackSearchSessionEnded: () => {} }
  }

  const { track } = usePendo()
  const { analytics } = useCookieConsent()

  const now = options.now ?? Date.now
  const random = options.random ?? Math.random
  const analyticsState: AnalyticsState = {
    lastRenderedSearch: null,
    lastEmittedSignature: null,
    lastFailedSignature: null,
  }

  watch(
    [() => getCurrentSearchState(options), analytics],
    ([state, analyticsGranted]) => {
      if (!analyticsGranted) {
        return
      }
      processSearchState(state, analyticsState, track, now, random)
    },
    { immediate: true },
  )

  if (getCurrentInstance()) {
    const endSession = () => finalizeCurrentSearchSession(analyticsState, track, now)
    // Current search page is not keep-alive; normal unmount removes this pagehide listener.
    window.addEventListener('pagehide', endSession)
    onBeforeUnmount(() => {
      window.removeEventListener('pagehide', endSession)
      endSession()
    })
  }

  return {
    trackResultClicked: (result: SearchDiagnosticResult, rank: number) =>
      trackSearchResultClicked(result, rank, options, analyticsState, track),
    trackSearchSessionEnded: () => finalizeCurrentSearchSession(analyticsState, track, now),
  }
}

function getCurrentSearchState(options: SearchDiagnosticsAnalyticsOptions): CurrentSearchState {
  return {
    query: toValue(options.query),
    page: toValue(options.page),
    routeSearchSignature: toValue(options.routeSearchSignature),
    isLoading: toValue(options.isLoading),
    results: toValue(options.results),
    totalResults: toValue(options.totalResults),
    rows: toValue(options.rows),
    hasError: options.hasError ? toValue(options.hasError) : false,
    errorStatus: options.errorStatus ? toValue(options.errorStatus) ?? null : null,
    transportRetried: options.transportRetried ? toValue(options.transportRetried) : false,
    filterCount: options.filterCount ? toValue(options.filterCount) : 0,
    filterSignature: options.filterSignature ? toValue(options.filterSignature) : '',
    sortOrder: normalizeSortOrder(options.sortOrder ? toValue(options.sortOrder) : ''),
  }
}

function processSearchState(
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  track: TrackFunction,
  now: () => number,
  random: () => number,
) {
  const normalizedQuery = state.query.trim()
  if (state.isLoading) {
    return
  }
  if (state.hasError) {
    processFailedSearch(state, analyticsState, track, now, normalizedQuery)
    return
  }
  if (!shouldTrackRenderedState(normalizedQuery)) {
    analyticsState.lastFailedSignature = null
    finalizeInvalidQuerySession(analyticsState, track, now)
    return
  }
  analyticsState.lastFailedSignature = null
  processRenderedSearch(state, analyticsState, track, now, random, normalizedQuery)
}

function processRenderedSearch(
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  track: TrackFunction,
  now: () => number,
  random: () => number,
  normalizedQuery: string,
) {
  const signature = buildSearchSignature(state)
  if (signature === analyticsState.lastEmittedSignature) {
    return
  }

  const renderedAt = now()
  const searchId = createSearchId(() => renderedAt, random)
  const reformulated = isReformulation(analyticsState.lastRenderedSearch, normalizedQuery)

  emitReformulatedIfNeeded(state, analyticsState, track, normalizedQuery, searchId, renderedAt)
  finalizePreviousSearchForNextState(state, analyticsState, track, renderedAt, reformulated)
  emitRenderedSearch(state, track, searchId)
  emitNoResultsIfNeeded(state, track, searchId)
  storeRenderedSearch(state, analyticsState, normalizedQuery, signature, searchId, renderedAt)
}

function shouldTrackRenderedState(normalizedQuery: string): boolean {
  return normalizedQuery.length >= MIN_GLOBAL_SEARCH_LENGTH
}

function processFailedSearch(
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  track: TrackFunction,
  now: () => number,
  normalizedQuery: string,
) {
  if (normalizedQuery.length < MIN_GLOBAL_SEARCH_LENGTH) {
    return
  }

  const signature = buildFailedSearchSignature(state, normalizedQuery)
  if (signature === analyticsState.lastFailedSignature) {
    return
  }

  const reformulated = isReformulation(analyticsState.lastRenderedSearch, normalizedQuery)
  const ended = finalizePreviousSearchForNextState(state, analyticsState, track, now(), reformulated, false)
  if (ended) {
    clearRenderedSearchState(analyticsState)
  }

  analyticsState.lastFailedSignature = signature
  track(SEARCH_DIAGNOSTICS_EVENTS.failed, buildSearchFailedPayload({
    query: state.query,
    page: state.page,
    statusCode: state.errorStatus,
    errorType: classifySearchError(state.errorStatus),
    hasFilters: state.filterCount > 0,
    filterCount: state.filterCount,
    sortOrder: state.sortOrder,
  }))
}

function emitReformulatedIfNeeded(
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  track: TrackFunction,
  normalizedQuery: string,
  searchId: string,
  renderedAt: number,
) {
  const previous = analyticsState.lastRenderedSearch
  if (!previous || previous.normalizedQuery === normalizedQuery) {
    return
  }

  track(SEARCH_DIAGNOSTICS_EVENTS.reformulated, buildSearchReformulatedPayload({
    previousSearchId: previous.searchId,
    newSearchId: searchId,
    previousQuery: previous.query,
    newQuery: state.query,
    previousTotalResults: previous.totalResults,
    previousResultCountOnPage: previous.resultCountOnPage,
    previousClickedResult: previous.clickedResult,
    timeSincePreviousSearchMs: renderedAt - previous.renderedAt,
  }))
}

function isReformulation(previous: RenderedSearchState | null, normalizedQuery: string): boolean {
  return !!previous && previous.normalizedQuery !== normalizedQuery
}

function emitRenderedSearch(state: CurrentSearchState, track: TrackFunction, searchId: string) {
  track(SEARCH_DIAGNOSTICS_EVENTS.resultsRendered, buildSearchResultsRenderedPayload({
    searchId,
    query: state.query,
    page: state.page,
    rows: state.rows,
    totalResults: state.totalResults,
    results: state.results,
    transportRetried: state.transportRetried,
  }))
}

function emitNoResultsIfNeeded(state: CurrentSearchState, track: TrackFunction, searchId: string) {
  if (state.page !== 1 || state.results.length > 0 || state.totalResults > 0) {
    return
  }

  track(SEARCH_DIAGNOSTICS_EVENTS.noResults, buildSearchNoResultsPayload({
    searchId,
    query: state.query,
    page: state.page,
    totalResults: state.totalResults,
    resultCountOnPage: state.results.length,
  }))
}

function storeRenderedSearch(
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  normalizedQuery: string,
  signature: string,
  searchId: string,
  renderedAt: number,
) {
  analyticsState.lastEmittedSignature = signature
  analyticsState.lastFailedSignature = null
  analyticsState.lastRenderedSearch = {
    searchId,
    query: state.query,
    normalizedQuery,
    page: state.page,
    renderedAt,
    totalResults: state.totalResults,
    resultCountOnPage: state.results.length,
    clickedResult: false,
    resultClickCount: 0,
    firstClickedRank: null,
    filterCount: state.filterCount,
    filterSignature: state.filterSignature,
    sortOrder: state.sortOrder,
    sessionEnded: false,
  }
}

function trackSearchResultClicked(
  result: SearchDiagnosticResult,
  rank: number,
  options: SearchDiagnosticsAnalyticsOptions,
  analyticsState: AnalyticsState,
  track: TrackFunction,
) {
  const renderedSearch = analyticsState.lastRenderedSearch
  if (!renderedSearch || renderedSearch.sessionEnded) {
    return
  }

  const isFirstClickForSearch = !renderedSearch.clickedResult

  track(SEARCH_DIAGNOSTICS_EVENTS.resultClicked, buildSearchResultClickedPayload({
    searchId: renderedSearch.searchId,
    query: renderedSearch.query,
    result,
    rank,
    page: renderedSearch.page,
    totalResults: renderedSearch.totalResults,
    resultCountOnPage: renderedSearch.resultCountOnPage,
    hasLicense: toValue(options.hasLicense),
    isFirstClickForSearch,
  }))

  renderedSearch.clickedResult = true
  renderedSearch.resultClickCount += 1
  renderedSearch.firstClickedRank ??= rank
}
